(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(root);
  } else {
    root.HLA_FAVORITES_PAGE = factory(root);
    const start = () => root.HLA_FAVORITES_PAGE.initAll();
    if (root.document && root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  const STORAGE_KEY = 'hla:favorites:v1';
  const CHANGE_EVENT = 'hla:favorites:change';
  const DEFAULT_TIMEOUT_MS = 6000;
  const productCache = new Map();

  function normalizeString(value) {
    return String(value || '').trim();
  }

  function normalizeHandle(value) {
    return normalizeString(value).toLowerCase();
  }

  function isPlainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value);
  }

  function readStorageFavorites() {
    try {
      const raw = root.localStorage && root.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return isPlainObject(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  function writeStorageFavorites(value) {
    try {
      if (root.localStorage) root.localStorage.setItem(STORAGE_KEY, JSON.stringify(value || {}));
    } catch (_) {
      // Storage may be unavailable; page rendering remains best effort.
    }
  }

  function readFavorites() {
    const actions = root.HLA_CARD_ACTIONS;
    if (actions && typeof actions.favorites === 'function') {
      const value = actions.favorites();
      return isPlainObject(value) ? value : {};
    }
    return readStorageFavorites();
  }

  function savedEntries() {
    return Object.values(readFavorites())
      .filter(isPlainObject)
      .sort((a, b) => String(b.savedAt || '').localeCompare(String(a.savedAt || '')));
  }

  function dispatchFavoritesChange(action, item) {
    const detail = { action, item, favorites: readFavorites() };
    try {
      root.dispatchEvent(new root.CustomEvent(CHANGE_EVENT, { detail }));
    } catch (_) {
      try {
        const event = root.document.createEvent('CustomEvent');
        event.initCustomEvent(CHANGE_EVENT, false, false, detail);
        root.dispatchEvent(event);
      } catch (__) {
        // Cross-control sync is best effort when CustomEvent is unavailable.
      }
    }
  }

  function removeFavorite(key) {
    const actions = root.HLA_CARD_ACTIONS;
    if (actions && typeof actions.removeItem === 'function') {
      return actions.removeItem(key, 'remove');
    }

    const saved = readStorageFavorites();
    const item = saved[key];
    if (!item) return null;
    delete saved[key];
    writeStorageFavorites(saved);
    dispatchFavoritesChange('remove', item);
    return item;
  }

  function getCardBuilder() {
    return root.HLA_STYLES_THAT_PAIR || null;
  }

  function makeNode(doc, tagName, className, text) {
    const node = doc.createElement(tagName);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = Boolean(hidden);
  }

  function setBusy(rootNode, busy) {
    rootNode.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function fetchWithTimeout(url, timeoutMs) {
    const controller = typeof root.AbortController !== 'undefined' ? new root.AbortController() : null;
    const timer = controller ? root.setTimeout(() => controller.abort(), timeoutMs) : null;
    const options = {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    };
    if (controller) options.signal = controller.signal;

    return root.fetch(url, options).finally(() => {
      if (timer) root.clearTimeout(timer);
    });
  }

  async function fetchProduct(handle, timeoutMs) {
    const normalized = normalizeHandle(handle);
    if (!normalized) return null;
    if (productCache.has(normalized)) return productCache.get(normalized);

    const builder = getCardBuilder();
    const url = builder && typeof builder.buildProductJsonUrl === 'function'
      ? builder.buildProductJsonUrl(normalized)
      : `/products/${encodeURIComponent(normalized)}.js`;
    const request = fetchWithTimeout(url, timeoutMs || DEFAULT_TIMEOUT_MS)
      .then(response => (response.ok ? response.json() : null))
      .catch(() => null);
    productCache.set(normalized, request);
    return request;
  }

  function favoriteKeyForItem(item, data) {
    return normalizeString(item.key)
      || [item.productId || data.productId, item.variantId || data.variantId].filter(Boolean).join(':')
      || data.handle
      || item.handle;
  }

  function addRemoveControl(card, doc, key) {
    const target = card.querySelector('.card__information')
      || card.querySelector('.card__content')
      || card;
    const wrap = makeNode(doc, 'div', 'hla-favorites-page__card-tools');
    const button = makeNode(doc, 'button', 'link hla-favorites-page__remove', 'Remove');
    button.type = 'button';
    button.dataset.hlaFavoritesRemove = '';
    button.setAttribute('aria-label', 'Remove saved style');
    wrap.appendChild(button);
    target.appendChild(wrap);
    card.dataset.hlaFavoriteKey = key;
  }

  async function renderProductEntry(item, index, options) {
    const builder = getCardBuilder();
    if (!builder || typeof builder.buildProductCard !== 'function' || typeof builder.productCardData !== 'function') {
      return null;
    }

    const handle = normalizeHandle(item.handle || item.productHandle || item.product_handle);
    if (!handle) return null;
    const product = await fetchProduct(handle, options.timeoutMs);
    if (!product) return null;

    const pair = {
      index,
      handle,
      title: normalizeString(item.title || product.title),
      url: normalizeString(item.url) || `/products/${handle}`,
      slot: 'favorite',
      slotLabel: 'Favorite',
      reasons: [],
      isCounterpart: false,
    };
    const data = builder.productCardData(product, pair);
    if (!data) return null;

    const card = builder.buildProductCard(product, pair, options.doc);
    if (!card) return null;
    const key = favoriteKeyForItem(item, data);
    addRemoveControl(card, options.doc, key);
    return {
      node: card,
      items: typeof builder.cardAddItems === 'function' ? builder.cardAddItems([card]) : [],
    };
  }

  function cartItemsFromLook(item) {
    const pieces = Array.isArray(item && item.look && item.look.pieces) ? item.look.pieces : [];
    return pieces
      .map(piece => ({ id: Number(piece.variantId || piece.variant_id), quantity: 1 }))
      .filter(piece => Number.isFinite(piece.id) && piece.id > 0);
  }

  function renderLookEntry(item, options) {
    const doc = options.doc;
    const key = normalizeString(item.key || (item.look && item.look.id));
    if (!key) return null;
    const pieces = Array.isArray(item.look && item.look.pieces) ? item.look.pieces : [];
    const firstPiece = pieces[0] || {};
    const article = makeNode(doc, 'article', 'hla-favorites-page__look-card');
    article.dataset.hlaFavoriteKey = key;

    const imageLink = makeNode(doc, 'a', 'hla-favorites-page__look-image');
    imageLink.href = normalizeString(item.url || firstPiece.url) || '#';
    if (item.image) {
      const image = makeNode(doc, 'img', '');
      image.src = item.image;
      image.alt = item.title || 'Saved look';
      image.loading = 'lazy';
      imageLink.appendChild(image);
    }
    article.appendChild(imageLink);

    const body = makeNode(doc, 'div', 'hla-favorites-page__look-body');
    const heading = makeNode(doc, 'h3', '', item.title || 'Saved look');
    body.appendChild(heading);
    const priceLine = [item.look && item.look.subtotal, item.look && item.look.packSubtotal]
      .filter(Boolean)
      .join(' / ');
    body.appendChild(makeNode(doc, 'p', '', priceLine || 'Saved look'));
    body.appendChild(makeNode(doc, 'p', 'card__availability card__availability--in-stock', 'Saved'));

    const actions = makeNode(doc, 'div', 'hla-favorites-page__look-actions');
    const quickView = makeNode(doc, 'button', 'button button--secondary', 'Quick View');
    quickView.type = 'button';
    quickView.dataset.hlaFavoritesLookQuick = '';
    const remove = makeNode(doc, 'button', 'link hla-favorites-page__remove', 'Remove');
    remove.type = 'button';
    remove.dataset.hlaFavoritesRemove = '';
    actions.appendChild(quickView);
    actions.appendChild(remove);
    body.appendChild(actions);
    article.appendChild(body);

    return {
      node: article,
      items: cartItemsFromLook(item),
      quickViewItem: {
        productId: firstPiece.productId || '',
        handle: firstPiece.handle || '',
        title: firstPiece.title || item.title || 'Saved look',
        url: firstPiece.url || item.url || '',
        variantId: firstPiece.variantId || '',
        sku: firstPiece.sku || '',
        unitPrice: firstPiece.unitPrice || '',
        packPrice: firstPiece.packPrice || '',
        image: item.image || '',
      },
    };
  }

  function refreshCardActions() {
    const actions = root.HLA_CARD_ACTIONS;
    if (actions && typeof actions.refresh === 'function') actions.refresh();
  }

  function replaceChildren(node, children) {
    if (!node) return;
    if (typeof node.replaceChildren === 'function') {
      node.replaceChildren(...children);
      return;
    }
    while (node.firstChild) node.removeChild(node.firstChild);
    children.forEach(child => node.appendChild(child));
  }

  function displayEmpty(controller, message) {
    const { grid, empty, addAll, status } = controller.nodes;
    replaceChildren(grid, []);
    setHidden(grid, true);
    setHidden(empty, false);
    if (status) status.textContent = message || 'No saved styles yet.';
    if (addAll) {
      addAll.disabled = true;
      addAll.dataset.state = 'idle';
    }
    controller.cartItems = [];
    controller.lookQuickViews = new Map();
  }

  function displayCards(controller, cards, message) {
    const { grid, empty, addAll, status } = controller.nodes;
    replaceChildren(grid, cards.map(card => card.node));
    setHidden(grid, false);
    setHidden(empty, true);
    if (status) status.textContent = message;
    controller.cartItems = cards.flatMap(card => card.items || []);
    controller.lookQuickViews = new Map(cards
      .filter(card => card.quickViewItem && card.node && card.node.dataset.hlaFavoriteKey)
      .map(card => [card.node.dataset.hlaFavoriteKey, card.quickViewItem]));
    if (addAll) {
      addAll.disabled = controller.cartItems.length === 0;
      addAll.dataset.state = 'idle';
    }
    refreshCardActions();
  }

  async function render(controller) {
    const renderId = ++controller.renderId;
    const rootNode = controller.rootNode;
    setBusy(rootNode, true);

    const entries = savedEntries();
    if (!entries.length) {
      displayEmpty(controller, 'No saved styles yet.');
      rootNode.dataset.hlaFavoritesState = 'empty';
      setBusy(rootNode, false);
      return [];
    }

    const options = {
      doc: rootNode.ownerDocument || root.document,
      timeoutMs: controller.timeoutMs,
    };
    const rendered = await Promise.all(entries.map((item, index) => {
      if (item.type === 'look') return Promise.resolve(renderLookEntry(item, options));
      return renderProductEntry(item, index, options);
    }));

    if (renderId !== controller.renderId) return controller.cards;

    const cards = rendered.filter(Boolean);
    const missingCount = entries.length - cards.length;
    controller.cards = cards;

    if (!cards.length) {
      displayEmpty(controller, missingCount ? 'Saved styles are unavailable right now.' : 'No saved styles yet.');
      rootNode.dataset.hlaFavoritesState = 'empty';
      setBusy(rootNode, false);
      return [];
    }

    const noun = cards.length === 1 ? 'saved style' : 'saved styles';
    const message = missingCount
      ? `${cards.length} ${noun}; ${missingCount} unavailable style skipped.`
      : `${cards.length} ${noun}`;
    displayCards(controller, cards, message);
    rootNode.dataset.hlaFavoritesState = 'ready';
    setBusy(rootNode, false);
    return cards;
  }

  function scheduleRender(controller) {
    if (controller.pendingRender) return controller.pendingRender;
    controller.pendingRender = Promise.resolve().then(() => render(controller)).finally(() => {
      controller.pendingRender = null;
    });
    return controller.pendingRender;
  }

  async function addAll(controller, button) {
    const builder = getCardBuilder();
    const items = controller.cartItems || [];
    if (!items.length || !builder || typeof builder.addItemsToCart !== 'function') return [];
    await builder.addItemsToCart(items, button);
    return items;
  }

  function bindController(controller) {
    const rootNode = controller.rootNode;
    rootNode.addEventListener('click', event => {
      const addAllButton = event.target.closest('[data-hla-favorites-add-all]');
      if (addAllButton) {
        event.preventDefault();
        addAll(controller, addAllButton);
        return;
      }

      const lookQuick = event.target.closest('[data-hla-favorites-look-quick]');
      if (lookQuick) {
        event.preventDefault();
        const card = lookQuick.closest('[data-hla-favorite-key]');
        const item = card && controller.lookQuickViews.get(card.dataset.hlaFavoriteKey);
        const actions = root.HLA_CARD_ACTIONS;
        if (item && actions && typeof actions.openQuickViewItem === 'function') {
          actions.openQuickViewItem(item, lookQuick);
        }
        return;
      }

      const removeButton = event.target.closest('[data-hla-favorites-remove]');
      if (removeButton) {
        event.preventDefault();
        const card = removeButton.closest('[data-hla-favorite-key]');
        const key = card && card.dataset.hlaFavoriteKey;
        if (!key) return;
        removeFavorite(key);
        scheduleRender(controller);
      }
    });

    if (root.addEventListener) {
      root.addEventListener(CHANGE_EVENT, () => scheduleRender(controller));
      root.addEventListener('storage', event => {
        if (!event || event.key === STORAGE_KEY) scheduleRender(controller);
      });
    }
  }

  function initPage(rootNode) {
    if (!rootNode || rootNode.dataset.hlaFavoritesPageInit === 'true') return rootNode && rootNode.hlaFavoritesPage;
    rootNode.dataset.hlaFavoritesPageInit = 'true';
    const controller = {
      rootNode,
      nodes: {
        grid: rootNode.querySelector('[data-hla-favorites-grid]'),
        empty: rootNode.querySelector('[data-hla-favorites-empty]'),
        status: rootNode.querySelector('[data-hla-favorites-status]'),
        addAll: rootNode.querySelector('[data-hla-favorites-add-all]'),
      },
      cards: [],
      cartItems: [],
      lookQuickViews: new Map(),
      renderId: 0,
      pendingRender: null,
      timeoutMs: Number(rootNode.dataset.timeoutMs) || DEFAULT_TIMEOUT_MS,
    };
    rootNode.hlaFavoritesPage = controller;
    bindController(controller);
    controller.ready = render(controller);
    return controller;
  }

  function initAll(doc = root.document) {
    if (!doc) return [];
    return Array.from(doc.querySelectorAll('[data-hla-favorites-page]')).map(initPage);
  }

  return {
    STORAGE_KEY,
    addAll,
    cartItemsFromLook,
    initAll,
    initPage,
    readFavorites,
    removeFavorite,
  };
});
